// Mission step 3: Recurrence configuration
// Manages recurrence settings for the mission
// CREATED: Part of unified MissionModal architecture
// CHANGE: Added initialRecurrence prop to properly load existing recurrence data in edit mode

import { RecurrenceConfigForm, type RecurrenceConfig } from './RecurrenceConfigForm';
import type { RecurrenceRule } from '../../lib/supabase';

interface MissionStep3RecurrenceProps {
  isRecurrent: boolean;
  onIsRecurrentChange: (value: boolean) => void;
  recurrenceConfig: RecurrenceConfig;
  onRecurrenceConfigChange: (config: RecurrenceConfig) => void;
  recurrenceError: string;
  loading: boolean;
  initialRecurrence?: RecurrenceRule | null;
}

export function MissionStep3Recurrence({
  isRecurrent,
  onIsRecurrentChange,
  recurrenceConfig,
  onRecurrenceConfigChange,
  recurrenceError,
  loading,
  initialRecurrence,
}: MissionStep3RecurrenceProps) {
  return (
    <div className="space-y-5 animate-fade-in">
      {/* Recurrence toggle */}
      <div>
        <label htmlFor="is-recurrent" className="block text-sm font-medium text-gray-700 mb-3">
          Mission récurrente
        </label>
        <button
          type="button"
          id="is-recurrent"
          role="switch"
          aria-checked={isRecurrent}
          onClick={() => onIsRecurrentChange(!isRecurrent)}
          disabled={loading}
          className={`relative inline-flex h-8 w-20 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
            isRecurrent ? 'bg-primary-600' : 'bg-gray-300'
          }`}
          style={{ transition: 'background-color 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)' }}
        >
          <span className="sr-only">Toggle récurrence</span>
          <span
            className={`flex items-center justify-center h-6 w-10 transform rounded-full bg-white transition-all duration-300 font-medium text-xs ${
              isRecurrent ? 'translate-x-9' : 'translate-x-1'
            }`}
            style={{ transition: 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)' }}
          >
            {isRecurrent ? (
              <span className="text-primary-600">Oui</span>
            ) : (
              <span className="text-gray-600">Non</span>
            )}
          </span>
        </button>
      </div>

      {/* Recurrence configuration */}
      {isRecurrent ? (
        <div className="pt-4">
          <RecurrenceConfigForm
            initialRule={initialRecurrence}
            disabled={loading}
            showPreview={true}
            onChange={onRecurrenceConfigChange}
            errorMessage={recurrenceError}
          />
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-sm text-gray-600">
            Cette mission sera créée comme une mission unique, sans récurrence.
          </p>
        </div>
      )}
    </div>
  );
}
